import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';

import { CanvasElement, CanvasElementId, getRootElementId } from '@blaze-runtime/engine-domain';
import { syncSelect } from '@blaze-shared/platform-data-access';

import { EngineState, getEngineState } from '../../+state/engine';
import { isElementType } from '../../canvas-elements/element-model/element-type-guards';
import { ContainerDefinition } from '../../elements/container/ai/container.definition';
import { ElementDefinitionsService } from '../../services/element-definitions.service';

import { ResolvedChildDefinition } from './definition-mapper';
import { PageLayout } from './page-layout.model';

@Injectable({ providedIn: 'root' })
export class PageLayoutSerializerService {
  private readonly elementDefinitionsService = inject(ElementDefinitionsService);
  private readonly store = inject(Store);

  getPageContent(elements: CanvasElement[], page: { id: string; name: string }): PageLayout {
    const elementMap = new Map<CanvasElementId, CanvasElement>(
      elements.map((element) => [element.id, element]),
    );
    const state = syncSelect(this.store, getEngineState);

    const containers = this.findRootContainers(elements, page.id, page.name);
    const containerDefinitions: ContainerDefinition[] = [];

    for (const container of containers) {
      const def = this.resolveDefinition(container, elementMap, state);
      if (def && this.isContainerDefinition(def)) {
        containerDefinitions.push(def);
      }
    }

    return {
      page: {
        name: page.name,
        children: containerDefinitions,
      },
      componentWorkflows: {},
    };
  }

  private resolveDefinition(
    element: CanvasElement,
    elementMap: Map<CanvasElementId, CanvasElement>,
    state: EngineState,
  ): ResolvedChildDefinition | null {
    const definition = this.elementDefinitionsService.extractAiDefinition(element, state.schemas);
    if (!definition) {
      return null;
    }

    if (
      this.elementDefinitionsService.getFeature(
        element.type,
        (feature) => feature.ai?.description === true,
      )
    ) {
      definition.aiDescription = element.description;
    }

    definition.description = element.description || element.name;

    const mapper = this.elementDefinitionsService.resolveDefinitionMapper(element.type);
    const slots = element.slots;

    if (mapper?.assembleChildren && slots?.ids) {
      const slotChildren = slots.ids.map((slotId) =>
        this.resolveChildList(slots[slotId]?.children ?? [], elementMap, state),
      );
      mapper.assembleChildren(definition, slotChildren);
    }

    return definition;
  }

  private resolveChildList(
    childIds: string[],
    elementMap: Map<CanvasElementId, CanvasElement>,
    state: EngineState,
  ): ResolvedChildDefinition[] {
    return childIds
      .map((id) => elementMap.get(id))
      .filter((el): el is CanvasElement => !!el)
      .map((el) => this.resolveDefinition(el, elementMap, state))
      .filter((def): def is ResolvedChildDefinition => !!def);
  }

  private findRootContainers(
    elements: CanvasElement[],
    pageId: string,
    pageName: string,
  ): CanvasElement[] {
    const rootId = getRootElementId(pageId);
    const root = elements.find((el) => el.id === rootId);

    if (!root) {
      throw new Error(`Couldn't find root element for page ${pageName}`);
    }

    if (root.slots?.main?.children) {
      return root.slots.main.children
        .map((id) => elements.find((element) => element.id === id))
        .filter((element): element is CanvasElement => isElementType(element, 'container'));
    }

    return elements.filter((el) => el.parentNode?.id === rootId && isElementType(el, 'container'));
  }

  private isContainerDefinition(def: ResolvedChildDefinition): def is ContainerDefinition {
    return def.$type === 'container' || (def.$type as string) === 'row';
  }
}
